import React, {useEffect, useMemo, useState, useRef} from 'react'
import axios from 'axios'
import MakeupRequestModal from '../../modules/makeup/MakeupRequestModal'
import {KTSVG} from '../../../_metronic/helpers'
import {useAuth} from '../../modules/auth/core/Auth'
import {CalendarExportButton, createCalendarDateTime} from '../../utils/calendarExport'

// 1) YYYY-MM-DD üretimi: ofset çıkarma yok, yerel takvimden oku
const formatDateKey = (date: Date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const API_URL = process.env.REACT_APP_API_URL
const GET_SCHEDULE_URL = `${API_URL}/Custom/GetInstructorSchedule`
const START_ONLINE_ATTENDANCE_URL = `${API_URL}/Custom/StartOnlineAttendance`
const GET_ONLINE_ATTENDANCE_STATUS_URL = `${API_URL}/Custom/GetInstructorOnlineAttendanceStatus`
const UPDATE_ONLINE_ATTENDANCE_STATUS_URL = `${API_URL}/Custom/UpdateInstructorOnlineAttendanceStatus`
const GET_COURSE_STUDENT_SUMMARIES_URL = `${API_URL}/Custom/GetInstructorCourseStudentAttendanceSummaries`
const PRINT = {WIDTH: 1122, HEIGHT: 794, MARGIN: 40} // A4 landscape @96dpi

// 2) Sunucudan gelen tarihleri yerel güne sabitle
// (ISO "YYYY-MM-DDTHH:mm:ss" veya "YYYY-MM-DD" fark etmez)
const parseLocalDate = (str: string) => {
  const dt = new Date(str) // utanmadan parse et
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()) // yerel güne pinle
}

const timeTextToMinutes = (timeText: string) => {
  const [h, m] = timeText.split(':').map((a) => Number(a))
  return h * 60 + m
}

interface ScheduleItem {
  id: number
  courseId?: number
  courseScheduleId?: number
  isOnlineAttendanceActive?: boolean
  day: number
  startTime: string
  endTime: string
  courseName: string
  courseCode?: string
  classroomName: string
  departmentName?: string
  name?: string
  detail?: string
  isMakeup?: boolean
  date?: string
}

interface AttendanceStudent {
  studentId: number
  fullName: string
  studentNumber?: string
  photoUrl?: string | null
  isPresent: boolean
  isLockedByInstructor: boolean
  statusText: string
}

interface AttendanceState {
  hasActiveSession: boolean
  hasSession?: boolean
  isOnlineAttendanceActive?: boolean
  isStudentJoinEnabled?: boolean
  isDailyBlock?: boolean
  joinedStudentCount?: number
  totalStudentCount?: number
  sessionId?: number
  studentDeadlineAt?: string
  students?: AttendanceStudent[]
}

interface CourseStudentSummary {
  studentId: number
  fullName: string
  studentNumber?: string
  photoUrl?: string | null
  presentCount: number
  totalCourseScheduleCount: number
  attendanceRate: number
  maxPossibleAttendanceRate?: number
}
const InstructorWeeklySchedule: React.FC = () => {
  const {currentUser} = useAuth()
  const [schedule, setSchedule] = useState<ScheduleItem[]>([])
  const [semesterStart, setSemesterStart] = useState<Date | null>(null)
  const [semesterEnd, setSemesterEnd] = useState<Date | null>(null)
  const [weekIndex, setWeekIndex] = useState(0)
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 576)
  const [showMakeup, setShowMakeup] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [printScale, setPrintScale] = useState(1)
  const [isPrinting, setIsPrinting] = useState(false)
  const [attendanceCourseScheduleId, setAttendanceCourseScheduleId] = useState<number | null>(null)
  const [attendanceReferenceDate, setAttendanceReferenceDate] = useState<string | null>(null)
  const [attendanceConsecutiveCourseScheduleIds, setAttendanceConsecutiveCourseScheduleIds] =
    useState<number[]>([])
  const [applyToConsecutiveSlots, setApplyToConsecutiveSlots] = useState(false)
  const [isStudentJoinEnabled, setIsStudentJoinEnabled] = useState(true)
  const [attendanceState, setAttendanceState] = useState<AttendanceState | null>(null)
  const [attendanceLoading, setAttendanceLoading] = useState(false)
  const [summaryCourseScheduleId, setSummaryCourseScheduleId] = useState<number | null>(null)
  const [studentSummaries, setStudentSummaries] = useState<CourseStudentSummary[]>([])
  const [summaryLoading, setSummaryLoading] = useState(false)
  const attendanceRequestIdRef = useRef(0)

  const mergeAttendanceStudents = (
    previousStudents: AttendanceStudent[] | undefined,
    nextStudents: AttendanceStudent[] | undefined
  ): AttendanceStudent[] | undefined => {
    if (!nextStudents) {
      return previousStudents
    }

    if (!previousStudents || previousStudents.length === 0) {
      return nextStudents
    }

    const previousById = new Map(previousStudents.map((student) => [student.studentId, student]))

    return nextStudents.map((student) => {
      const previous = previousById.get(student.studentId)
      if (!previous) {
        return student
      }

      const isUnchanged =
        previous.fullName === student.fullName &&
        previous.studentNumber === student.studentNumber &&
        previous.photoUrl === student.photoUrl &&
        previous.isPresent === student.isPresent &&
        previous.isLockedByInstructor === student.isLockedByInstructor &&
        previous.statusText === student.statusText

      return isUnchanged ? previous : student
    })
  }

  const COLORS = [
    '#FFCDD2',
    '#F8BBD0',
    '#E1BEE7',
    '#D1C4E9',
    '#C5CAE9',
    '#BBDEFB',
    '#B3E5FC',
    '#B2EBF2',
    '#B2DFDB',
    '#C8E6C9',
    '#DCEDC8',
    '#F0F4C3',
    '#FFF9C4',
    '#FFECB3',
    '#FFE0B2',
    '#FFCCBC',
    '#D7CCC8',
    '#F5F5F5',
    '#CFD8DC',
  ]

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const afterPrint = () => {
      setIsPrinting(false)
      setPrintScale(1)
    }
    window.addEventListener('afterprint', afterPrint)
    return () => window.removeEventListener('afterprint', afterPrint)
  }, [])

  useEffect(() => {
    axios
      .get(GET_SCHEDULE_URL)
      .then((res) => {
        setSchedule(res.data.items || [])
        setSemesterStart(res.data.semesterStart ? parseLocalDate(res.data.semesterStart) : null)
        setSemesterEnd(res.data.semesterEnd ? parseLocalDate(res.data.semesterEnd) : null)
      })
      .catch(console.error)
  }, [])

  const days = useMemo(() => {
    const arr: Date[] = []
    if (semesterStart && semesterEnd) {
      const d = new Date(semesterStart)
      while (d <= semesterEnd) {
        arr.push(new Date(d))
        d.setDate(d.getDate() + 1)
      }
    }
    return arr
  }, [semesterStart, semesterEnd])

  const weeks = useMemo(() => {
    const result: Date[][] = []
    let week: Date[] = []
    days.forEach((date, idx) => {
      week.push(date)
      const isLast = idx === days.length - 1
      if (date.getDay() === 0 || isLast) {
        result.push(week)
        week = []
      }
    })
    return result
  }, [days])

  useEffect(() => {
    if (!weeks.length) {
      setWeekIndex(0)
      return
    }

    const today = new Date()
    const todayKey = formatDateKey(today)
    const currentWeekIndex = weeks.findIndex((week) =>
      week.some((date) => formatDateKey(date) === todayKey)
    )

    setWeekIndex(currentWeekIndex >= 0 ? currentWeekIndex : 0)
  }, [weeks])

  const itemsByDate = useMemo(() => {
    const map: Record<string, ScheduleItem[]> = {}
    days.forEach((date) => {
      const key = formatDateKey(date)
      map[key] = []
      const dow = date.getDay() === 0 ? 7 : date.getDay()
      schedule.forEach((item) => {
        if (item.date) {
          const dkey = formatDateKey(new Date(item.date))
          if (dkey === key) map[key].push(item)
        } else if (item.day === dow) {
          map[key].push({...item})
        }
      })
    })
    return map
  }, [days, schedule])

  const currentWeek = weeks[weekIndex] || []

  const weekDays = useMemo(() => {
    return currentWeek.filter((date) => {
      const key = formatDateKey(date)
      const items = itemsByDate[key] || []
      const dow = date.getDay()
      if (items.length === 0 && (dow === 0 || dow === 6)) {
        return false
      }
      return true
    })
  }, [currentWeek, itemsByDate])

  const courseColors = useMemo(() => {
    const map = new Map<string, string>()
    let i = 0
    schedule.forEach((item) => {
      const key = item.courseCode || item.courseName
      if (!map.has(key)) {
        map.set(key, COLORS[i % COLORS.length])
        i++
      }
    })
    return map
  }, [schedule])

  const fullName = useMemo(() => {
    if (!currentUser) return ''
    const parts = [currentUser.title, currentUser.fullName].filter(Boolean)
    return parts.join(' ')
  }, [currentUser])

  const calendarEvents = useMemo(() => {
    return days.flatMap((date) => {
      const key = formatDateKey(date)
      return (itemsByDate[key] || []).map((item) => ({
        uid: `instructor-course-${item.courseScheduleId || item.id}-${key}-${item.startTime}`,
        title: item.courseCode ? `${item.courseName} (${item.courseCode})` : item.courseName,
        start: createCalendarDateTime(key, item.startTime),
        end: createCalendarDateTime(key, item.endTime),
        location: item.classroomName,
        description: [
          item.departmentName,
          item.name?.split(' - ').pop(),
          item.detail,
          item.isMakeup ? 'Telafi' : null,
        ]
          .filter(Boolean)
          .join('\n'),
      }))
    })
  }, [days, itemsByDate])

  const handlePrint = () => {
    const el = containerRef.current
    if (!el) {
      window.print()
      return
    }

    // İçeriğin gerçek ölçüsünü al
    const rect = el.getBoundingClientRect()
    const printableW = PRINT.WIDTH - PRINT.MARGIN * 2
    const printableH = PRINT.HEIGHT - PRINT.MARGIN * 2

    const scale = Math.min(printableW / rect.width, printableH / rect.height, 1)

    setPrintScale(scale)
    setIsPrinting(true)

    // Ölçek stilinin uygulanması ve reflow için 2 frame bekle
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print()
      })
    })
  }

  const loadAttendanceState = (courseScheduleId: number, referenceDate?: string | null) => {
    const requestId = ++attendanceRequestIdRef.current
    setAttendanceLoading(true)
    axios
      .get(GET_ONLINE_ATTENDANCE_STATUS_URL, {params: {courseScheduleId, referenceDate}})
      .then((res) => {
        if (requestId !== attendanceRequestIdRef.current) {
          return
        }

        const nextState = res.data as AttendanceState
        setAttendanceState((previousState) => {
          if (!previousState) {
            return nextState
          }

          return {
            ...nextState,
            students: mergeAttendanceStudents(previousState.students, nextState.students),
          }
        })
      })
      .finally(() => {
        if (requestId === attendanceRequestIdRef.current) {
          setAttendanceLoading(false)
        }
      })
  }

  useEffect(() => {
    if (!attendanceCourseScheduleId || !attendanceState?.hasActiveSession) {
      return
    }

    const intervalId = window.setInterval(() => {
      loadAttendanceState(attendanceCourseScheduleId, attendanceReferenceDate)
    }, 10000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [attendanceCourseScheduleId, attendanceReferenceDate, attendanceState?.hasActiveSession])

  const startAttendance = (durationMinutes: number) => {
    if (!attendanceCourseScheduleId) return
    if (!attendanceState?.isOnlineAttendanceActive) return
    setAttendanceLoading(true)
    axios
      .post(START_ONLINE_ATTENDANCE_URL, {
        courseScheduleId: attendanceCourseScheduleId,
        durationMinutes,
        applyToConsecutiveSlots:
          applyToConsecutiveSlots && attendanceConsecutiveCourseScheduleIds.length > 1,
        consecutiveCourseScheduleIds: attendanceConsecutiveCourseScheduleIds,
        isStudentJoinEnabled,
        referenceDate: attendanceReferenceDate,
      })
      .then(() => loadAttendanceState(attendanceCourseScheduleId, attendanceReferenceDate))
      .finally(() => setAttendanceLoading(false))
  }

  const updateStudentAttendance = (studentId: number, isPresent: boolean) => {
    if (!attendanceState?.sessionId) return
    setAttendanceLoading(true)
    axios
      .post(UPDATE_ONLINE_ATTENDANCE_STATUS_URL, {
        sessionId: attendanceState.sessionId,
        studentId,
        isPresent,
      })
      .then(() => {
        if (attendanceCourseScheduleId) {
          loadAttendanceState(attendanceCourseScheduleId, attendanceReferenceDate)
        }
      })
      .finally(() => setAttendanceLoading(false))
  }

  const loadCourseStudentSummaries = (courseScheduleId: number, week: number, day: number) => {
    setSummaryLoading(true)
    axios
      .get(GET_COURSE_STUDENT_SUMMARIES_URL, {params: {courseScheduleId, week, day}})
      .then((res) => {
        setStudentSummaries((res.data?.students || []) as CourseStudentSummary[])
      })
      .finally(() => setSummaryLoading(false))
  }

  return (
    <div
      ref={containerRef}
      className='container mt-4 schedule-print'
      style={{
        transform: !isPrinting && printScale !== 1 ? `scale(${printScale})` : undefined,
        transformOrigin: 'top left',
        width: isPrinting ? `${PRINT.WIDTH - PRINT.MARGIN * 2}px` : undefined,
        height: isPrinting ? `${PRINT.HEIGHT - PRINT.MARGIN * 2}px` : undefined,
        overflow: isPrinting ? 'hidden' : undefined,
        ['--print-scale' as any]: String(isPrinting ? printScale : 1),
      }}
    >
      <style>
        {`
  :root {
    --print-scale: 1;
  }
  @media print {
    @page { size: A4 landscape; margin: 10mm; }
    html, body { width: 297mm; height: 210mm; margin: 0; font-size: 12px; }
    /* Yazdırma sırasında aynı ölçeği uygula */
    .schedule-print {
      font-size: 12px;
      transform: scale(var(--print-scale));
      transform-origin: top left;
      width: ${PRINT.WIDTH - PRINT.MARGIN * 2}px !important;
      height: ${PRINT.HEIGHT - PRINT.MARGIN * 2}px !important;
      overflow: hidden !important;
      margin: 0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .schedule-print * { font-size: inherit !important; }
    .makeup-item, .makeup-group, .noprint { display: none !important; }
  }
`}
      </style>
      <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4'>
        <h2 className='mb-0'>{fullName ? `${fullName} haftalık programı` : 'Haftalık Programı'}</h2>
        <CalendarExportButton
          events={calendarEvents}
          fileName={fullName ? `${fullName}-haftalik-program` : 'haftalik-program'}
          className='noprint'
        />
      </div>
      <div className='alert alert-primary noprint'>
        Değerli öğretim elemanları, ortak derslerde dersin sorumlusu olarak dersin koordinatörü
        görünmektedir. OBS sisteminde dersin not sorumlusu kim ise derslik sisteminde de onun adı
        çıkmaktadır. Bu durumdaki öğretim elemanlarının normal şartlarda herhangi bir işlem yapması
        gerekmemektedir. Ancak programda dersi fiili olarak veren öğretim elemanının görünmesi
        gerektiği durumlarda öğrenci işleri ile irtibata geçerek durumu iletebilirsiniz.
      </div>
      <div className='d-flex justify-content-between align-items-center mb-3 noprint'>
        <button
          className='btn btn-light btn-sm'
          disabled={weekIndex === 0}
          onClick={() => setWeekIndex((w) => w - 1)}
        >
          Önceki Hafta
        </button>
        <span className='fw-bold'>{weekIndex + 1}. Hafta</span>
        <div>
          <button
            className='btn btn-light btn-sm me-2'
            disabled={weekIndex >= weeks.length - 1}
            onClick={() => setWeekIndex((w) => w + 1)}
          >
            Sonraki Hafta
          </button>
          <button className='btn btn-primary btn-sm' onClick={handlePrint}>
            Yazdır
          </button>
        </div>
      </div>
      <div className='d-flex flex-column flex-sm-row'>
        {weekDays.map((date) => {
          const key = formatDateKey(date)
          const items = itemsByDate[key] || []

          const courseGroups = Array.from(
            items.reduce((acc, item) => {
              const ck = item.courseCode || item.courseName
              if (!acc.has(ck)) acc.set(ck, [] as ScheduleItem[])
              acc.get(ck)!.push(item)
              return acc
            }, new Map<string, ScheduleItem[]>())
          )

          const dayWidth = isMobile ? '100%' : `${100 / weekDays.length}%`
          return (
            <div
              key={key}
              className='border border-2 rounded me-sm-2 mb-2 mb-sm-0 flex-shrink-0'
              style={{width: dayWidth}}
            >
              <div className='bg-light text-center fw-bold p-2'>
                {isPrinting
                  ? date.toLocaleDateString('tr-TR', {weekday: 'long'})
                  : date.toLocaleDateString('tr-TR', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'numeric',
                    })}
              </div>
              {courseGroups.length > 0 ? (
                courseGroups.map(([ck, items]) => {
                  const color = courseColors.get(ck) || '#ccc'
                  const isMakeup = items.some((i) => i.isMakeup)
                  const allMakeup = items.every((i) => i.isMakeup)
                  const firstRegularItem = items.find((item) => !item.isMakeup)
                  const regularCourseScheduleId = firstRegularItem?.courseScheduleId
                  const regularCourseId = firstRegularItem?.courseId
                  const isOnlineAttendanceActive = !!firstRegularItem?.isOnlineAttendanceActive
                  return (
                    <div key={ck} className={`p-2 border-top ${allMakeup ? 'makeup-group' : ''}`}>
                      <div
                        className='p-2 rounded border'
                        style={{borderColor: color, backgroundColor: `${color}33`}}
                      >
                        <div className='fw-semibold'>
                          {items[0].courseCode
                            ? `${items[0].courseName} (${items[0].courseCode})`
                            : items[0].courseName}
                          {isMakeup && (
                            <span className='badge bg-warning text-dark ms-1 noprint'>Telafi</span>
                          )}
                        </div>
                        {items.map((g, idx) => (
                          <div
                            key={idx}
                            className={`text-muted small mb-1 ${g.isMakeup ? 'makeup-item' : ''}`}
                          >
                            {g.startTime} - {g.endTime} {g.classroomName}
                            {g.departmentName && ` - ${g.departmentName}`}
                            {g.name && ` - ${g.name.split(' - ').pop()}`}
                          </div>
                        ))}
                        <div className='noprint mt-2 pt-2 border-top d-flex justify-content-end flex-wrap gap-2 bg-white bg-opacity-50 rounded px-2'>
                          {regularCourseScheduleId && isOnlineAttendanceActive && (
                            <button
                              className='btn btn-icon btn-sm btn-success'
                              title='Çevrimiçi Yoklama'
                              onClick={() => {
                                const regularSchedules = items
                                  .filter((a) => !a.isMakeup && !!a.courseScheduleId)
                                  .sort(
                                    (a, b) =>
                                      timeTextToMinutes(a.startTime) -
                                      timeTextToMinutes(b.startTime)
                                  )

                                const hasConsecutiveSlots =
                                  regularSchedules.length > 1 &&
                                  regularSchedules.every((a, i) => {
                                    if (i === 0) return true
                                    return (
                                      timeTextToMinutes(regularSchedules[i - 1].endTime) ===
                                      timeTextToMinutes(a.startTime)
                                    )
                                  })

                                const consecutiveIds = hasConsecutiveSlots
                                  ? regularSchedules.map((a) => a.courseScheduleId as number)
                                  : [regularCourseScheduleId]

                                setAttendanceCourseScheduleId(regularCourseScheduleId)
                                setAttendanceReferenceDate(key)
                                setAttendanceConsecutiveCourseScheduleIds(consecutiveIds)
                                setApplyToConsecutiveSlots(false)
                                setIsStudentJoinEnabled(true)
                                setAttendanceState(null)
                                loadAttendanceState(regularCourseScheduleId, key)
                              }}
                            >
                              <i className='bi bi-clipboard-check fs-4'></i>
                            </button>
                          )}
                          {regularCourseScheduleId && (
                            <button
                              className='btn btn-icon btn-sm btn-light-info'
                              title='Ders Öğrenci Listesi'
                              onClick={() => {
                                const selectedDay = date.getDay() === 0 ? 7 : date.getDay()
                                setSummaryCourseScheduleId(regularCourseScheduleId)
                                loadCourseStudentSummaries(
                                  regularCourseScheduleId,
                                  weekIndex + 1,
                                  selectedDay
                                )
                              }}
                            >
                              <i className='bi bi-people-fill fs-4'></i>
                            </button>
                          )}
                          {regularCourseId && (
                            <button
                              className='btn btn-icon btn-sm btn-light-primary'
                              title='Telafi Talebi'
                              onClick={() => {
                                setSelectedCourse(regularCourseId)
                                setShowMakeup(true)
                              }}
                            >
                              <KTSVG
                                path='/media/icons/duotune/arrows/arr029.svg'
                                className='svg-icon-3'
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className='p-2 text-muted'>Ders yok</div>
              )}
            </div>
          )
        })}
      </div>

      {attendanceCourseScheduleId && (
        <div className='modal d-block' role='dialog'>
          <div className='modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Çevrimiçi Yoklama Yönetimi</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    setAttendanceCourseScheduleId(null)
                    setAttendanceReferenceDate(null)
                    setAttendanceConsecutiveCourseScheduleIds([])
                    setApplyToConsecutiveSlots(false)
                    setIsStudentJoinEnabled(true)
                    setAttendanceState(null)
                    attendanceRequestIdRef.current += 1
                  }}
                ></button>
              </div>
              <div className='modal-body'>
                {attendanceState?.hasSession && (
                  <div className='alert alert-info mb-3 py-2'>
                    Katılan Öğrenci: {attendanceState.joinedStudentCount || 0} /{' '}
                    {attendanceState.totalStudentCount || 0}
                  </div>
                )}

                {!attendanceState?.isOnlineAttendanceActive && (
                  <div className='alert alert-warning mt-3 mb-0'>
                    Bu ders için çevrimiçi yoklama pasif. Bu derste online yoklama kullanılamaz.
                  </div>
                )}

                {!attendanceState?.hasActiveSession && (
                  <>
                    {attendanceConsecutiveCourseScheduleIds.length > 1 && (
                      <div className='form-check mt-3'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='attendanceConsecutiveSlotCheckbox'
                          checked={applyToConsecutiveSlots}
                          onChange={(e) => setApplyToConsecutiveSlots(e.target.checked)}
                        />
                        <label
                          className='form-check-label'
                          htmlFor='attendanceConsecutiveSlotCheckbox'
                        >
                          Bugünkü peş peşe ders saatlerinin tamamı için tek yoklama başlat
                        </label>
                      </div>
                    )}

                    <div className='form-check mt-3'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='attendanceStudentJoinCheckbox'
                        checked={isStudentJoinEnabled}
                        onChange={(e) => setIsStudentJoinEnabled(e.target.checked)}
                      />
                      <label className='form-check-label' htmlFor='attendanceStudentJoinCheckbox'>
                        Öğrenciler Katılabilsin
                      </label>
                    </div>

                    <div className='d-flex flex-wrap gap-2 mt-3'>
                      <button
                        className='btn btn-primary'
                        disabled={!attendanceState?.isOnlineAttendanceActive}
                        onClick={() => startAttendance(5)}
                      >
                        5 Dakika
                      </button>
                      <button
                        className='btn btn-primary'
                        disabled={!attendanceState?.isOnlineAttendanceActive}
                        onClick={() => startAttendance(10)}
                      >
                        10 Dakika
                      </button>
                      <button
                        className='btn btn-primary'
                        disabled={!attendanceState?.isOnlineAttendanceActive}
                        onClick={() => startAttendance(15)}
                      >
                        15 Dakika
                      </button>
                    </div>
                  </>
                )}
                {attendanceState?.hasSession && (
                  <>
                    <div className='text-muted small mt-2'>
                      Öğrenci Katılımı: {attendanceState.isStudentJoinEnabled ? 'Açık' : 'Kapalı'}
                    </div>
                    <div className='table-responsive mt-3'>
                      <table className='table table-sm align-middle'>
                        <thead>
                          <tr>
                            <th>Öğrenci</th>
                            <th>Durum</th>
                            <th>İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(attendanceState.students || []).map((student) => (
                            <tr key={student.studentId}>
                              <td>
                                <div className='d-flex align-items-center gap-2'>
                                  <div
                                    className='rounded-circle bg-light overflow-hidden flex-shrink-0 d-inline-flex align-items-center justify-content-center'
                                    style={{width: 40, height: 40}}
                                  >
                                    {student.photoUrl ? (
                                      <img
                                        src={student.photoUrl}
                                        alt={student.fullName}
                                        className='w-100 h-100'
                                        style={{objectFit: 'cover'}}
                                      />
                                    ) : (
                                      <span className='text-muted fw-bold'>
                                        {student.fullName.trim().charAt(0).toUpperCase() || '?'}
                                      </span>
                                    )}
                                  </div>
                                  <div>
                                    <div className='fw-semibold'>{student.fullName}</div>
                                    <div className='text-muted small'>
                                      No: {student.studentNumber || '-'}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    student.isPresent ? 'bg-success' : 'bg-secondary'
                                  }`}
                                >
                                  {student.isPresent ? '✔ Katıldı' : 'Henüz katılmadı'}
                                </span>
                              </td>
                              <td>
                                <button
                                  className='btn btn-sm btn-light-primary'
                                  onClick={() =>
                                    updateStudentAttendance(student.studentId, !student.isPresent)
                                  }
                                >
                                  Durumu Değiştir
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                {attendanceLoading && <div className='text-muted mt-2'>Yükleniyor...</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {summaryCourseScheduleId && (
        <div className='modal d-block' role='dialog'>
          <div className='modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Ders Öğrenci Özeti</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    setSummaryCourseScheduleId(null)
                    setStudentSummaries([])
                  }}
                ></button>
              </div>
              <div className='modal-body'>
                {summaryLoading && <div className='text-muted mt-2'>Yükleniyor...</div>}
                {!summaryLoading && (
                  <div className='table-responsive'>
                    <table className='table table-sm align-middle'>
                      <thead>
                        <tr>
                          <th>Öğrenci</th>
                          <th>Yoklama Katılım Yüzdesi</th>
                          <th>Maksimum Ulaşılabilir Yüzde</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studentSummaries.map((student) => (
                          <tr key={student.studentId}>
                            <td>
                              <div className='d-flex align-items-center gap-2'>
                                <div
                                  className='rounded-circle bg-light overflow-hidden flex-shrink-0 d-inline-flex align-items-center justify-content-center'
                                  style={{width: 40, height: 40}}
                                >
                                  {student.photoUrl ? (
                                    <img
                                      src={student.photoUrl}
                                      alt={student.fullName}
                                      className='w-100 h-100'
                                      style={{objectFit: 'cover'}}
                                    />
                                  ) : (
                                    <span className='text-muted fw-bold'>
                                      {student.fullName.trim().charAt(0).toUpperCase() || '?'}
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <div className='fw-semibold'>{student.fullName}</div>
                                  <div className='text-muted small'>
                                    No: {student.studentNumber || '-'}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className='fw-semibold'>%{student.attendanceRate}</div>
                              <div className='text-muted small'>
                                {student.presentCount} / {student.totalCourseScheduleCount}
                              </div>
                            </td>
                            <td>
                              <div className='fw-semibold'>
                                %{student.maxPossibleAttendanceRate ?? 0}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {!studentSummaries.length && (
                          <tr>
                            <td colSpan={3} className='text-muted'>
                              Öğrenci bulunamadı.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <MakeupRequestModal
        show={showMakeup}
        courseId={selectedCourse}
        onClose={() => setShowMakeup(false)}
      />
    </div>
  )
}

export default InstructorWeeklySchedule
